import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { RESEARCH_PAGE, PORTFOLIO_ITEMS } from '../lib/constants';
import { Button } from '../components/ui/Button';
import styles from './ResearchPage.module.css';

export const ResearchPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language === 'en' ? 'en' : 'it') as 'it' | 'en';

  useDocumentMetadata(t('research.meta_title'), t('research.meta_desc'));

  const relatedProjects = PORTFOLIO_ITEMS.filter((project) =>
    RESEARCH_PAGE.caseStudies.includes(project.id)
  );

  return (
    <div className={styles.page}>
      {/* ─── Hero Header ─── */}
      <section className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className="container">
          <div className={styles.heroContent}>
            <Link to="/" className={styles.backLink}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('segments.back_to_home')}
            </Link>

            <div className={styles.painBadge}>
              <span className={styles.badgePulse} />
              {RESEARCH_PAGE.pain[lang]}
            </div>

            <h1 className={styles.title}>{RESEARCH_PAGE.title[lang]}</h1>
            <p className={styles.subtitle}>{RESEARCH_PAGE.subtitle[lang]}</p>
          </div>
        </div>
      </section>

      {/* ─── Main Details Section ─── */}
      <section className={styles.detailsSection}>
        <div className="container">
          <div className={styles.contentContainer}>
            {/* Overview & Benefits Grid */}
            <div className={styles.grid2Col}>
              <div className={styles.overviewBox}>
                <h2 className={styles.sectionTitle}>{t('research.overview_label')}</h2>
                <div className={styles.longDescText}>
                  <p>{RESEARCH_PAGE.longDesc[lang]}</p>
                </div>
              </div>

              <div className={styles.benefitsBox}>
                {RESEARCH_PAGE.benefits.map((benefit, idx) => (
                  <div key={idx} className={styles.benefitCard}>
                    <div className={styles.benefitHeader}>
                      <span className={styles.checkIconWrapper}>
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <h3 className={styles.benefitTitle}>{benefit.title[lang]}</h3>
                    </div>
                    <p className={styles.benefitDesc}>{benefit.desc[lang]}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Services Checklist Box */}
            <div className={styles.servicesContainer}>
              <div className={styles.servicesHeader}>
                <div className={styles.servicesIconWrapper}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h2 className={styles.servicesTitle}>{t('research.services_title')}</h2>
              </div>
              <div className={styles.servicesGrid}>
                {RESEARCH_PAGE.services.map((service, idx) => (
                  <div key={idx} className={styles.serviceItem}>
                    <svg className={styles.serviceArrow} width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className={styles.serviceText}>{service[lang]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Portfolio Projects */}
            {relatedProjects.length > 0 && (
              <div className={styles.portfolioContainer}>
                <h2 className={styles.portfolioSectionTitle}>{t('segments.case_studies_title')}</h2>
                <div className={styles.portfolioGrid}>
                  {relatedProjects.map((project) => (
                    <div key={project.id} className={styles.portfolioCard}>
                      {project.thumbnail && (
                        <div className={styles.portfolioThumbWrapper}>
                          <img
                            src={project.thumbnail}
                            alt={project.title[lang]}
                            className={styles.portfolioThumb}
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className={styles.portfolioCardContent}>
                        <span className={styles.portfolioCategory}>{project.category[lang]}</span>
                        <h3 className={styles.portfolioTitle}>{project.title[lang]}</h3>
                        <p className={styles.portfolioDesc}>{project.desc[lang]}</p>
                        <div className={styles.portfolioTags}>
                          {project.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className={styles.portfolioTag}>
                              {tag}
                            </span>
                          ))}
                        </div>
                        <Link to={`/portfolio/${project.id}`} className={styles.portfolioLink}>
                          <span>{lang === 'it' ? 'Leggi il Case Study' : 'Read the Case Study'}</span>
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L19 10m0 0l-5.5 5.5M19 10H5" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Section */}
            <div className={styles.ctaCard}>
              <div className={styles.ctaGlow} />
              <h3 className={styles.ctaTitle}>{t('research.cta_title')}</h3>
              <p className={styles.ctaDesc}>{t('research.cta_desc')}</p>
              <div className={styles.ctaActions}>
                <Button variant="primary" href="/#contact">
                  {t('research.cta_btn')}
                </Button>
                <Button variant="secondary" href="/">
                  {t('segments.back_to_home')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
